/**
 * Supplier Service
 *
 * CRUD operations connecting to backend API.
 */

import { Supplier, SupplierFormData, SupplierListFilters, PaginatedSupplierResponse, SupplierType, SupplierStatus, PaymentTermType, AgreementKind, ServiceCostModel } from '../types';
import { apiClient } from '@/shared/services/api';

// ── Backend types ─────────────────────────────────────────────────────────────

interface BackendSupplier {
  id: string;
  location: string;
  name: string;
  legalName?: string;
  type: 'products' | 'services' | 'both';
  status: 'active' | 'inactive';
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  taxId?: string;
  paymentTerms?: string;
  agreementKind?: string;
  agreementRate?: string | number | null;
  defaultCostModel?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// Backend type → frontend types array
// 'both' expands a dos tipos; 'products' y 'services' son singulares
function backendTypeToFrontend(type: BackendSupplier['type']): SupplierType[] {
  if (type === 'both') return ['merchandise', 'services'];
  if (type === 'services') return ['services'];
  return ['merchandise'];
}

// Frontend types array → backend single type
// Si incluye ambos → 'both'; si solo services → 'services'; de lo contrario → 'products'
function frontendTypesToBackend(types: SupplierType[]): BackendSupplier['type'] {
  const hasMerchandise = types.some(t => t === 'merchandise' || t === 'general');
  const hasServices = types.includes('services');
  if (hasMerchandise && hasServices) return 'both';
  if (hasServices) return 'services';
  return 'products';
}

function mapBackendSupplier(b: BackendSupplier): Supplier {
  const contacts = b.contactName
    ? [{ id: `${b.id}-c1`, name: b.contactName, email: b.contactEmail, phone: b.contactPhone, isPrimary: true }]
    : [];
  return {
    id: b.id,
    name: b.name,
    legalName: b.legalName || undefined,
    types: backendTypeToFrontend(b.type),
    status: (b.status as SupplierStatus) ?? 'active',
    taxId: b.taxId || undefined,
    locationId: b.location,
    address: b.address || undefined,
    contacts,
    paymentTerms: b.paymentTerms ? { type: b.paymentTerms as PaymentTermType, currency: 'EUR' } : undefined,
    agreementKind: (b.agreementKind as AgreementKind) || undefined,
    agreementRate: b.agreementRate != null && b.agreementRate !== '' ? Number(b.agreementRate) : undefined,
    defaultCostModel: (b.defaultCostModel as ServiceCostModel) || undefined,
    notes: b.notes || undefined,
    createdAt: new Date(b.createdAt),
    updatedAt: new Date(b.updatedAt),
  };
}

/** Get all suppliers */
export async function getAllSuppliers(): Promise<Supplier[]> {
  const response = await apiClient.get<{ results: BackendSupplier[]; count: number }>(
    '/api/v1/suppliers/',
    { params: { page_size: 200 } }
  );
  return response.data.results.map(mapBackendSupplier);
}

/** Get supplier by ID */
export async function getSupplierById(id: string): Promise<Supplier | undefined> {
  const response = await apiClient.get<BackendSupplier>(`/api/v1/suppliers/${id}/`);
  return mapBackendSupplier(response.data);
}

/**
 * Compose address parts into a single address string for the backend
 * (which only has one `address` text field).
 */
function composeAddress(data: Partial<SupplierFormData>): string {
  const parts = [data.address, data.city, data.state, data.postalCode, data.country].filter(Boolean);
  return parts.join(', ');
}

/** Create a new supplier */
export async function createSupplier(data: SupplierFormData, locationId: string): Promise<Supplier> {
  const primaryContact = data.contacts?.find(c => c.isPrimary) ?? data.contacts?.[0];
  const payload = {
    name: data.name,
    legal_name: data.legalName ?? '',
    type: frontendTypesToBackend(data.types ?? []),
    status: data.status ?? 'active',
    contact_name: primaryContact?.name ?? '',
    contact_email: primaryContact?.email ?? data.email ?? '',
    contact_phone: primaryContact?.phone ?? data.phone ?? '',
    address: composeAddress(data),
    tax_id: data.taxId ?? '',
    notes: data.notes ?? '',
    payment_terms: data.paymentTerms?.type ?? '',
    agreement_kind: data.agreementKind ?? 'none',
    agreement_rate: data.agreementRate ?? null,
    default_cost_model: data.defaultCostModel ?? '',
    location: locationId,
  };
  const response = await apiClient.post<BackendSupplier>('/api/v1/suppliers/', payload);
  return mapBackendSupplier(response.data);
}

/** Update an existing supplier */
export async function updateSupplier(id: string, data: Partial<SupplierFormData>): Promise<Supplier> {
  const payload: Record<string, unknown> = {};
  if (data.name !== undefined) payload.name = data.name;
  if (data.legalName !== undefined) payload.legal_name = data.legalName;
  if (data.status !== undefined) payload.status = data.status;
  if (data.notes !== undefined) payload.notes = data.notes;
  if (data.taxId !== undefined) payload.tax_id = data.taxId;
  // Compose address from parts (backend only has a single address text field)
  if (data.address !== undefined || data.city !== undefined || data.state !== undefined || data.postalCode !== undefined || data.country !== undefined) {
    payload.address = composeAddress(data);
  }
  if (data.types !== undefined) payload.type = frontendTypesToBackend(data.types);
  if (data.paymentTerms?.type !== undefined) payload.payment_terms = data.paymentTerms.type;
  if (data.agreementKind !== undefined) payload.agreement_kind = data.agreementKind;
  if (data.agreementRate !== undefined) payload.agreement_rate = data.agreementRate ?? null;
  if (data.defaultCostModel !== undefined) payload.default_cost_model = data.defaultCostModel;

  // Map contacts → contact_name/email/phone; fall back to supplier-level email/phone
  if (data.contacts) {
    const primary = data.contacts.find(c => c.isPrimary) ?? data.contacts[0];
    if (primary) {
      payload.contact_name = primary.name;
      payload.contact_email = primary.email ?? data.email ?? '';
      payload.contact_phone = primary.phone ?? data.phone ?? '';
    }
  } else if (data.email !== undefined || data.phone !== undefined) {
    if (data.email !== undefined) payload.contact_email = data.email;
    if (data.phone !== undefined) payload.contact_phone = data.phone;
  }

  const response = await apiClient.patch<BackendSupplier>(`/api/v1/suppliers/${id}/`, payload);
  return mapBackendSupplier(response.data);
}

/** Delete a supplier */
export async function deleteSupplier(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/suppliers/${id}/`);
}

/** Paginated query with filters */
export async function getSuppliersPaginated(
  filters?: SupplierListFilters,
  cursor?: string,
  limit = 20,
): Promise<PaginatedSupplierResponse> {
  let endpoint: string;
  let params: Record<string, string | number | boolean> | undefined;

  if (cursor) {
    endpoint = cursor;
  } else {
    endpoint = '/api/v1/suppliers/';
    params = { page_size: limit };
    if (filters?.search) params.search = filters.search;
    if (filters?.status) params.status = filters.status;
  }

  const response = await apiClient.get<{
    results: BackendSupplier[];
    count: number;
    next: string | null;
  }>(endpoint, params ? { params } : {});

  return {
    data: response.data.results.map(mapBackendSupplier),
    nextCursor: response.data.next,
    hasMore: !!response.data.next,
    totalCount: response.data.count,
  };
}
